/**
 * © 2025 Grupo Salamander SAPI de CV
 *
 * Color palette definitions for the Renaissance design system.
 * Apple-inspired color system with light/dark mode variants.
 */

import type React from 'react'
import type { TDensity } from './tokens'

export type TPaletteColor = {
  main: string
  light: string
  dark: string
  /** Primary only: set solely when the URL forces a contrast colour. */
  forcedContrastText?: string
  /** Primary only: set solely when the URL forces an avatar colour. */
  avatarColor?: string
}

export type TPaletteDefinition = {
  white: TPaletteColor
  black: TPaletteColor
  gray: TPaletteColor
  gray2: TPaletteColor
  gray3: TPaletteColor
  gray4: TPaletteColor
  gray5: TPaletteColor
  gray6: TPaletteColor
  red: TPaletteColor
  orange: TPaletteColor
  yellow: TPaletteColor
  green: TPaletteColor
  mint: TPaletteColor
  teal: TPaletteColor
  cyan: TPaletteColor
  blue: TPaletteColor
  indigo: TPaletteColor
  purple: TPaletteColor
  pink: TPaletteColor
  brown: TPaletteColor
  background: TPaletteColor
  paper: TPaletteColor
  primary: TPaletteColor
  secondary: TPaletteColor
  text: TPaletteColor
  textSecondary: TPaletteColor
  textDisabled: TPaletteColor
  error: TPaletteColor
  success: TPaletteColor
  warning: TPaletteColor
  info: TPaletteColor
}

export interface ThemeProps {
  children: React.ReactNode
  /** Force a colour mode. Omit to follow `prefers-color-scheme` (the default everywhere but Storybook). */
  mode?: 'light' | 'dark'
  /** Information density. Only `regular` exists; the prop is the mechanism. */
  density?: TDensity
  /** Force reduced motion. Omit to follow `prefers-reduced-motion`. */
  reducedMotion?: boolean
  url?: {
    PrimaryLightColor?: string | null
    PrimaryDarkColor?: string | null
    SecondaryLightColor?: string | null
    SecondaryDarkColor?: string | null
    /** Colour of initials avatars; unset keeps the primary accent. */
    AvatarLightColor?: string | null
    AvatarDarkColor?: string | null
    /** Forces the text/icon colour painted ON the primary accent in light
     *  mode; unset keeps the automatic black/white pick (`onAccent`). */
    PrimaryContrastLightColor?: string | null
    /** Same as `PrimaryContrastLightColor`, for dark mode. */
    PrimaryContrastDarkColor?: string | null
  } | null
}

/**
 * Base color tokens — each color has a light and dark mode value.
 * `main` is resolved by UIPalette() based on the active mode.
 */
const baseColors = {
  white: {
    light: 'rgb(255,255,255)',
    dark: 'rgb(28,28,28)',
  },
  black: {
    light: 'rgb(36,36,36)',
    dark: 'rgb(255,255,255)',
  },
  /**
   * Secondary/neutral-grey text (`textSecondary` below, wired to
   * `theme.palette.text.secondary`) — the sidebar's connected-status text,
   * list/menu row subtitles (`UISegmentedControl`'s inactive tab among
   * them), KPI captions (`UIStat`'s muted line) and field labels all read
   * this. Both shades are WCAG AA (>=4.5:1) against every real background
   * they measured against (#220's real-browser axe run against the
   * component sheet and archetypes, not just the plain panel/tile/ground
   * pairing): panel/tile/ground (`Theme/tokens.ts` `surface`) plus the
   * tinted status/tab/chip fills several components render this on.
   *
   * First tried the redesign's own already-ported, canvas-approved
   * `tokens.ts` `text.light.secondary` (`#6e6e73`, replacing the older,
   * un-migrated iOS system grey `#8e8e93` — only 3.26:1 on white) — but
   * `#6e6e73` still measured serious on several of those tinted fills
   * (down to 3.97:1, e.g. `UISegmentedControl`'s inactive tab), so `light`
   * is darkened once more to clear the worst of those with margin (>=4.6:1
   * everywhere measured); `dark` is lightened the matching amount against
   * its own worst real dark fill (`ListDetailPanel`'s inline-tab
   * background, 3.49:1 at the original `#86868b`/`rgb(142,142,147)`).
   * Matches `tokens.ts` `text.secondary`'s own re-tuned values exactly
   * (same real-world backgrounds, same fix) — see that token's comment.
   */
  gray: {
    light: 'rgb(100,100,105)',
    dark: 'rgb(163,163,168)',
  },
  gray2: {
    light: 'rgb(174,174,178)',
    dark: 'rgb(99,102,102)',
  },
  gray3: {
    light: 'rgb(199,199,204)',
    dark: 'rgb(72,72,74)',
  },
  gray4: {
    light: 'rgb(209,209,214)',
    dark: 'rgb(58,58,60)',
  },
  gray5: {
    light: 'rgb(229,229,234)',
    dark: 'rgb(44,44,46)',
  },
  gray6: {
    light: 'rgb(242,242,247)',
    dark: 'rgb(28,28,30)',
  },
  /**
   * `error` below (field labels/notes in error state, the error icon, the
   * outlined/contained error button) — the un-tuned Apple systemRed only
   * clears 3.2-4.1:1 against the surfaces `UIField`'s error state and
   * `MuiButton`'s error variant actually render on (`surface.ground`/`tile`
   * light, `surface.tile` dark, #228's real-browser axe run). Replaced with
   * Apple's own "Red (Increase Contrast)" pair — the accessibility-mode
   * variant of the same colour, not an invented one — which clears AA with
   * margin against every one of those (>=4.83 light, >=4.94 dark).
   */
  red: {
    light: 'rgb(215,0,21)',
    dark: 'rgb(255,105,97)',
  },
  orange: {
    light: 'rgb(255,149,0)',
    dark: 'rgb(255,204,0)',
  },
  yellow: {
    light: 'rgb(255,204,0)',
    dark: 'rgb(255,214,10)',
  },
  green: {
    light: 'rgb(52,199,89)',
    dark: 'rgb(48,209,88)',
  },
  mint: {
    light: 'rgb(0,200,179)',
    dark: 'rgb(102,212,207)',
  },
  teal: {
    light: 'rgb(89,173,196)',
    dark: 'rgb(106,196,220)',
  },
  cyan: {
    light: 'rgb(85,190,240)',
    dark: 'rgb(90,200,245)',
  },
  /**
   * Only consumer left is `.UIButton-tab-active` (`Theme/overrides/buttons.ts`):
   * its text sits on `gray5.main`, and the plain Apple systemBlue only
   * cleared 2.8:1 (light) / 4.31:1 (dark) there (#228's real-browser axe
   * run). Apple's own "Blue (Increase Contrast)" pair clears AA with margin
   * (6.02 light, 4.92 dark) — safe to change globally since nothing else
   * reads this token.
   */
  blue: {
    light: 'rgb(0,64,221)',
    dark: 'rgb(64,156,255)',
  },
  indigo: {
    light: 'rgb(88,86,214)',
    dark: 'rgb(94,92,230)',
  },
  purple: {
    light: 'rgb(175,82,222)',
    dark: 'rgb(191,90,242)',
  },
  pink: {
    light: 'rgb(255,45,85)',
    dark: 'rgb(255,55,95)',
  },
  brown: {
    light: 'rgb(172,142,104)',
    dark: 'rgb(172,142,104)',
  },
} as const

function resolveColor(
  color: { light: string; dark: string },
  mode: 'dark' | 'light'
): TPaletteColor {
  return {
    main: mode === 'dark' ? color.dark : color.light,
    light: color.light,
    dark: color.dark,
  }
}

export function UIPalette(
  mode: 'dark' | 'light',
  url?: ThemeProps['url']
): TPaletteDefinition {
  const c = baseColors

  const white = resolveColor(c.white, mode)
  const black = resolveColor(c.black, mode)
  const gray = resolveColor(c.gray, mode)
  const gray2 = resolveColor(c.gray2, mode)
  const gray3 = resolveColor(c.gray3, mode)
  const gray4 = resolveColor(c.gray4, mode)
  const gray5 = resolveColor(c.gray5, mode)
  const gray6 = resolveColor(c.gray6, mode)
  const red = resolveColor(c.red, mode)
  const orange = resolveColor(c.orange, mode)
  const yellow = resolveColor(c.yellow, mode)
  const green = resolveColor(c.green, mode)
  const mint = resolveColor(c.mint, mode)
  const teal = resolveColor(c.teal, mode)
  const cyan = resolveColor(c.cyan, mode)
  const blue = resolveColor(c.blue, mode)
  const indigo = resolveColor(c.indigo, mode)
  const purple = resolveColor(c.purple, mode)
  const pink = resolveColor(c.pink, mode)
  const brown = resolveColor(c.brown, mode)

  const background: TPaletteColor = {
    main: mode === 'dark' ? 'rgb(0,0,0)' : gray6.light,
    light: gray6.light,
    dark: 'rgb(0,0,0)',
  }

  const paper: TPaletteColor = {
    main: mode === 'dark' ? gray6.dark : white.light,
    light: white.light,
    dark: gray6.dark,
  }

  let primary: TPaletteColor = { ...blue }
  let secondary: TPaletteColor = { ...blue }

  if (url) {
    const fallback = '#3296D4'
    primary = {
      main:
        mode === 'dark'
          ? url.PrimaryDarkColor || fallback
          : url.PrimaryLightColor || fallback,
      light: url.PrimaryLightColor || fallback,
      dark: url.PrimaryDarkColor || fallback,
    }
    secondary = {
      main:
        mode === 'dark'
          ? url.SecondaryDarkColor || fallback
          : url.SecondaryLightColor || fallback,
      light: url.SecondaryLightColor || fallback,
      dark: url.SecondaryDarkColor || fallback,
    }
  }

  const forcedContrast =
    (mode === 'dark'
      ? url?.PrimaryContrastDarkColor
      : url?.PrimaryContrastLightColor) || undefined
  const avatarColor =
    (mode === 'dark' ? url?.AvatarDarkColor : url?.AvatarLightColor) ||
    undefined
  primary = {
    ...primary,
    ...(forcedContrast && { forcedContrastText: forcedContrast }),
    ...(avatarColor && { avatarColor }),
  }

  return {
    white,
    black,
    gray,
    gray2,
    gray3,
    gray4,
    gray5,
    gray6,
    red,
    orange,
    yellow,
    green,
    mint,
    teal,
    cyan,
    blue,
    indigo,
    purple,
    pink,
    brown,
    background,
    paper,
    primary,
    secondary,
    text: { ...black },
    textSecondary: { ...gray },
    textDisabled: { ...gray3 },
    error: { ...red },
    success: { ...green },
    warning: { ...yellow },
    info: { ...purple },
  }
}
